import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Card,
    CardContent,
    Typography,
    Avatar,
    Grid,
    Chip,
    IconButton,
} from "@mui/material"
import {
    Visibility as VisibilityIcon,
    LocalPharmacy as PharmacyIcon,
    LocationOn as LocationIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    Close as CloseIcon,
} from "@mui/icons-material"
import {useTranslation} from "react-i18next"

export default function PharmacyListDialog({open, onClose, pharmacies, couponCode}) {
    const {t, i18n} = useTranslation()
    const pharmacyName = pharmacy => pharmacy[{en: "tenantNameEn", hy: "tenantNameHy", ru: "tenantNameRu"}[i18n.language?.slice(0, 2)]]
        || pharmacy.tenantNameEn || pharmacy.name || t("UnnamedPharmacy");

    const formatAddress = (address) => {
        if (!address) return t("AddressUnavailable")
        if (typeof address === "string") return address
        return `${address.street || ""}, ${address.city || ""}, ${address.country || ""}`.replace(/^,\s*|,\s*$/g, "")
    }

    const formatPhone = (phones) => {
        if (!Array.isArray(phones) || phones.length === 0) {
            return t("PhonesUnavailable");
        }
        return phones
            .map(p => `${p.phoneNumberCode || ""} ${p.phoneNumber || ""}`.trim())
            .join(", ");
    };


    const getPharmacyInitials = (name) => {
        if (!name) return "P"
        return name
            .split(" ")
            .map((word) => word[0])
            .join("")
            .substring(0, 2)
            .toUpperCase()
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            aria-labelledby="coupon-pharmacies-title"
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                },
            }}
        >
            <DialogTitle
                component="div"
                id="coupon-pharmacies-header"
                sx={{
                    backgroundColor: "primary.main",
                    color: "white",
                    position: "relative",
                    py: 1,
                }}
            >
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1}}>
                    <Box sx={{display: "flex", alignItems: "center", minWidth: 0}}>
                        <Avatar
                            sx={{
                                bgcolor: "rgba(255,255,255,0.2)",
                                mr: 2,
                                width: 40,
                                height: 40,
                            }}
                        >
                            <VisibilityIcon/>
                        </Avatar>
                        <Box sx={{minWidth: 0}}>
                            <Typography id="coupon-pharmacies-title" variant="h5" component="h2"
                                sx={{fontWeight: 600, mb: 0.5, fontSize: {xs: "1.1rem", sm: "1.5rem"}, overflowWrap: "anywhere"}}>
                                {t("Pharmacies")} {couponCode}
                            </Typography>
                            <Chip
                                label={`${pharmacies?.length || 0} ${t("Pharmacies")}`}
                                size="small"
                                sx={{
                                    bgcolor: "rgba(255,255,255,0.2)",
                                    color: "white",
                                    fontWeight: 500,
                                }}
                            />
                        </Box>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        aria-label={t("Close")}
                        sx={{
                            color: "white",
                            bgcolor: "rgba(255,255,255,0.1)",
                            "&:hover": {bgcolor: "rgba(255,255,255,0.2)"},
                        }}
                    >
                        <CloseIcon/>
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{p: 3, bgcolor: "#f8fafc"}}>
                {pharmacies?.length === 0 ? (
                    <Box
                        sx={{
                            textAlign: "center",
                            py: 6,
                            color: "text.secondary",
                        }}
                    >
                        <PharmacyIcon sx={{fontSize: 64, mb: 2, opacity: 0.3}}/>
                        <Typography variant="h6" gutterBottom>
                            {t("NoPharmaciesFound")}
                        </Typography>
                        <Typography
                            variant="body2">{t("NoPharmaciesFoundDesc")}</Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2} sx={{marginTop: 0, marginBottom: 1}}>
                        {pharmacies?.map((pharmacy, index) => (
                            <Grid item xs={12} sm={6} key={pharmacy.id || index}>
                                <Card
                                    sx={{
                                        height: "100%",
                                        transition: "all 0.3s ease",
                                        "&:hover": {
                                            transform: "translateY(-4px)",
                                            boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
                                        },
                                        borderRadius: 2,
                                        border: "1px solid",
                                        borderColor: "divider",
                                    }}
                                >
                                    <CardContent sx={{p: 2}}>
                                        <Box sx={{display: "flex", alignItems: "flex-start", mb: 2}}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: "primary.main",
                                                    mr: 2,
                                                    width: 48,
                                                    height: 48,
                                                    fontSize: "1.1rem",
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {getPharmacyInitials(pharmacyName(pharmacy))}
                                            </Avatar>
                                            <Box sx={{flex: 1, minWidth: 0}}>
                                                <Typography
                                                    variant="h6"
                                                    component="h3"
                                                    sx={{
                                                        fontWeight: 600,
                                                        mb: 0.5,
                                                        color: "text.primary",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {pharmacyName(pharmacy)}
                                                </Typography>
                                                <Chip
                                                    icon={<PharmacyIcon/>}
                                                    label={t("Pharmacy")}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{space: 2}}>
                                            <Box sx={{display: "flex", alignItems: "flex-start", mb: 2}}>
                                                <LocationIcon
                                                    sx={{
                                                        color: "text.secondary",
                                                        mr: 1.5,
                                                        mt: 0.5,
                                                        fontSize: 20,
                                                    }}
                                                />
                                                <Typography variant="body2" color="text.secondary"
                                                            sx={{lineHeight: 1.5}}>
                                                    {formatAddress(pharmacy.address)}
                                                </Typography>
                                            </Box>

                                            {pharmacy.phones && (
                                                <Box sx={{display: "flex", alignItems: "center", mb: 1}}>
                                                    <PhoneIcon
                                                        sx={{
                                                            color: "text.secondary",
                                                            mr: 1.5,
                                                            fontSize: 20,
                                                        }}
                                                    />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {formatPhone(pharmacy.phones)}
                                                    </Typography>
                                                </Box>
                                            )}

                                            {pharmacy.tenantEmail && (
                                                <Box sx={{display: "flex", alignItems: "center"}}>
                                                    <EmailIcon
                                                        sx={{
                                                            color: "text.secondary",
                                                            mr: 1.5,
                                                            fontSize: 20,
                                                        }}
                                                    />
                                                    <Typography variant="body2" color="text.secondary" sx={{overflowWrap: "anywhere", minWidth: 0}}>
                                                        {pharmacy.tenantEmail}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    p: 1,
                    bgcolor: "background.paper",
                    borderTop: "1px solid",
                    borderColor: "divider",
                }}
            >
                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{
                        px: 4,
                        py: 1,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                    }}
                >
                    {t("Close")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
